import type { PropsWithChildren } from 'react';
import { useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { config } from '~app/config';

import { useValidateQuery, AUTH_API_KEY } from '~entities/auth';
import type { AuthApi } from '~entities/auth';
import { isAuthorizedAtom } from '~entities/auth/model/session-atoms';

import { useApi } from '~shared/providers/use-api';
import { Loader } from '~shared/ui/loader/loader';

import { useAtom } from 'jotai';

export const AuthGuard = ({ children }: PropsWithChildren) => {
  const openingTime = useRef(new Date().getTime());
  const { pathname } = useLocation();
  const api = useApi<AuthApi>(AUTH_API_KEY);
  const [isAuthorized, setIsAuhorized] = useAtom(isAuthorizedAtom);

  const shouldReloadPage = () =>
    new Date().getTime() - openingTime.current >
    config.inactivityThreshold * 60 * 1000;

  useValidateQuery(api, {
    onSuccess: () => {
      setIsAuhorized(true);
    },
    onError: () => {
      setIsAuhorized(false);
    },
    enabled: isAuthorized === null,
  });

  const isLoginPage = pathname.startsWith('/login');

  // Initial page load. Auth status is not defined.
  // Waiting for validation query to check authentication status.
  if (isAuthorized === null) {
    return <Loader />;
  }

  // Trying to access authorized zone, but user is not authenticated -> redirect to login
  if (isAuthorized === false && !isLoginPage) {
    return <Navigate to="/login" replace />;
  }

  // Redirect authorized user to the page he tried to access or home page (if he originally opened login page)
  if (isAuthorized && isLoginPage) {
    if (shouldReloadPage()) {
      window.location.replace(`/?date=${new Date().getTime()}`);
      return null;
    }
    return <Navigate to="/" replace />;
  }

  // Render original requested page
  // For unauthorized user - login page
  // For authorized user - requested page
  return <>{children}</>;
};
